function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { num, usd } from "../sales/territory-common";

/*
 * §312 Content Hub Analytics — Overview (§4.1) + Campaign detail (§4.2) +
 * the performance-over-time chart with event pins (§4.6).
 * Spec: PlunkDocs specs/CONTENT_HUB_ANALYTICS.md; the mockup's amber notes
 * are the contract. Rails (probe-verified 2026-09-02, all live staging):
 *   GET /v1/content/funnel/aggregate/{range}?groupBy=portfolio|campaign|
 *       story|channel[&campaign=]   → { tier, groups:[{key,title,metrics…,
 *       channels:[{channel,metrics…}]}] }
 *   GET /v1/content/trend/{range}[?groupBy=campaign|…][&campaign=]
 *       → { tier, lanes:[{key,title,activeStart,activeEnd,
 *           series:[{date,metrics…}], events:[{date,channel,containerId,title}]}] }
 *   GET /v1/content/decay?$top=      → { items:[{containerId,title,metric,
 *           current,previous,dropPct}] }
 * NOTE: the groupBy param is the PLAIN name — `$groupBy=` is silently
 * ignored (probed; flagged in the § thread).
 *
 * GRADED RAILS (§2, hard rules): render exactly the stages the tier fills —
 * no $0, no dashes, no greyed columns. The sales-layer unlock hint renders
 * exactly ONCE, at the funnel's truncation point. Rankings label their
 * currency explicitly. Orders now ships alongside Revenue: the rollup grew an
 * order COUNT (content_stat_day.orders), populated by the sales-attribution
 * passes incl. imported-email recipient clicks (CONTENT_HUB_NEXT §6 part-2).
 *
 * MONEY GATE (defense in depth, unchanged from §310): every metric rendered
 * here comes from the explicit audience-metric allowlist below — ad
 * cost/spend/budget can never render regardless of payload. Revenue is the
 * tenant's OWN sales (always theirs to see), labeled last-touch · 30d.
 */

export const CHANNEL_COLORS = {
  email: "#378add",
  web: "#1d9e75",
  facebook: "#3b6fd6",
  instagram: "#8b5cf6",
  social: "#8b5cf6",
  blog: "#1d9e75",
  paid: "#e0a23a",
  google_ads: "#e0a23a",
  facebook_ads: "#e0a23a",
  constant_contact: "#378add"
};
export const channelColor = ch => CHANNEL_COLORS[String(ch || "").toLowerCase()] || "var(--plk-text-subtle)";
const chLabel = ch => String(ch || "").replace(/_/g, " ");
const day = d => d ? String(d).slice(0, 10) : null;
// "Aug 24" for chart labels and tooltips (dates arrive as yyyy-mm-dd).
const shortDay = d => {
  const k = day(d);
  if (!k) return "";
  const t = new Date(`${k}T12:00:00`);
  return isNaN(t) ? k : t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
};
const cell = v => Number(v) > 0 ? num(v) : "—";

// Tier-aware metric set (§4.6): only metrics the tier can fill.
export function tierMetrics(tier) {
  const m = [["engagements", "Engagements"]];
  if (tier >= 1) m.push(["sessions", "Visits"], ["conversions", "Conversions"]);
  if (tier >= 2) m.push(["orders", "Orders"], ["salesValue", "Sales"]);
  return m;
}
export function defaultMetric(tier) {
  return tier >= 1 ? "sessions" : "engagements";
}

// Ranking currency chain (§2): the tier currency first; when the primary is
// all-zero in the window the SORT falls back down the chain and the label
// says so (honest, not useless).
export function rankRows(rows, tier) {
  const chain = tier >= 2 ? ["salesValue", "orders", "conversions", "sessions", "engagements"] : tier >= 1 ? ["conversions", "sessions", "engagements"] : ["engagements"];
  let metric = chain[0];
  for (const m of chain) {
    if (rows.some(r => Number(r[m]) > 0)) {
      metric = m;
      break;
    }
  }
  const labels = {
    salesValue: "attributed revenue",
    orders: "orders",
    conversions: "conversions",
    sessions: "sessions",
    engagements: "engagements"
  };
  const sorted = [...rows].sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0));
  return {
    rows: sorted,
    metric,
    label: labels[metric],
    fellBack: metric !== chain[0],
    primaryLabel: labels[chain[0]]
  };
}
const noteStyle = {
  fontSize: "12px",
  color: "var(--plk-text-dim)",
  margin: 0
};

// ── §4.1 header: the portfolio funnel ────────────────────────────────────
export function FunnelStrip({
  group,
  tier,
  compact
}) {
  if (!group) return null;
  // Stages per tier — exactly what the rails fill, never padded.
  const stages = [["reach", "Reach", v => cell(v)], ["engagements", "Engagements", v => cell(v)]];
  if (tier >= 1) stages.push(["sessions", "Sessions", v => cell(v)], ["conversions", "Conversions", v => cell(v)]);
  if (tier >= 2) stages.push(["orders", "Orders", v => cell(v)], ["salesValue", "Revenue", v => Number(v) > 0 ? usd(v) : "—"]);
  const channels = group.channels || [];
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "stretch",
      gap: "6px",
      flexWrap: "wrap"
    }
  }, stages.map(([k, label, fmt], i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: k
  }, i > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      alignSelf: "center",
      color: "var(--plk-text-dim)"
    }
  }, "\u2192"), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: "1 1 120px",
      border: "1px solid var(--plk-border)",
      borderRadius: "10px",
      padding: "10px 12px",
      background: "var(--plk-surface)"
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: "10.5px",
      letterSpacing: ".05em",
      textTransform: "uppercase",
      color: "var(--plk-text-dim)",
      margin: "0 0 2px"
    }
  }, label, k === "salesValue" && /*#__PURE__*/React.createElement("span", {
    title: "Attributed via the person-level last-touch \xB7 30d convention",
    style: {
      cursor: "help"
    }
  }, " \u24D8")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: compact ? "16px" : "20px",
      fontWeight: 700,
      margin: 0
    }
  }, fmt(group[k])), !compact && channels.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: "4px",
      flexWrap: "wrap",
      marginTop: "6px"
    }
  }, channels.filter(c => Number(c[k]) > 0).map(c => /*#__PURE__*/React.createElement("span", {
    key: c.channel,
    title: `${chLabel(c.channel)}: ${k === "salesValue" ? usd(c[k]) : num(c[k])}`,
    style: {
      fontSize: "10px",
      borderRadius: "8px",
      padding: "1px 7px",
      color: "var(--plk-text-inverse)",
      background: channelColor(c.channel),
      textTransform: "capitalize"
    }
  }, chLabel(c.channel)))))))), tier < 2 &&
  /*#__PURE__*/
  // §2: the unlock hint — ONCE, at the truncation point, never a banner.
  React.createElement("p", {
    style: {
      ...noteStyle,
      marginTop: "6px",
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: "/Data",
    style: {
      color: "var(--plk-accent)"
    }
  }, tier < 1 ? "Link your website tracker to follow this content into sessions and conversions →" : "Link your sales transactions to see revenue attributed to every piece of content →")));
}

// ── §4.6 the trend chart with event pins ─────────────────────────────────
export class TrendChart extends React.Component {
  constructor(...args) {
    super(...args);
    // props: lane {series, events}, tier, metric, onMetric(m), height?
    _defineProperty(this, "state", {
      hover: null
    });
  }
  render() {
    const {
      lane,
      tier,
      metric,
      onMetric,
      height = 160
    } = this.props;
    // §4.6 overlays: campaign lanes overlaid on the portfolio line (up to 3),
    // each {key,title,series,color}. They share this chart's y-scale so the
    // comparison reads true (audience-compare pattern).
    const overlays = (this.props.overlays || []).filter(o => o && (o.series || []).length);
    const series = lane && lane.series || [];
    const events = lane && lane.events || [];
    const W = 940,
      H = height,
      padL = 46,
      padR = 10,
      padT = 10,
      padB = 22;
    if (!series.length) return /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "No day-level activity in this range.");
    const dates = series.map(p => day(p.date));
    const t0 = new Date(dates[0]).getTime(),
      t1 = new Date(dates[dates.length - 1]).getTime();
    const span = Math.max(1, t1 - t0);
    const x = d => padL + (new Date(day(d)).getTime() - t0) / span * (W - padL - padR);
    const vals = series.map(p => Number(p[metric]) || 0);
    const overlayVals = overlays.flatMap(o => (o.series || []).map(p => Number(p[metric]) || 0));
    const vmax = Math.max(1, ...vals, ...overlayVals);
    const y = v => padT + (1 - v / vmax) * (H - padT - padB);
    // Today is still being counted: draw its segment dashed and lighter so a partial day doesn't read as a drop.
    const todayKey = (() => {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    })();
    const partial = series.length > 1 && day(series[series.length - 1].date) === todayKey;
    const solid = partial ? series.slice(0, -1) : series;
    const path = solid.map((p, i) => `${i ? "L" : "M"}${x(p.date).toFixed(1)},${y(Number(p[metric]) || 0).toFixed(1)}`).join(" ");
    const tailPath = partial ? series.slice(-2).map((p, i) => `${i ? "L" : "M"}${x(p.date).toFixed(1)},${y(Number(p[metric]) || 0).toFixed(1)}`).join(" ") : "";
    const fmtV = v => metric === "salesValue" ? usd(v) : num(v);
    const gridV = [0, 0.5, 1].map(f => Math.round(vmax * f));
    const isMoney = metric === "salesValue";
    return /*#__PURE__*/React.createElement("div", null, onMetric && /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginBottom: "6px"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, tierMetrics(tier).map(([k, label]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: metric === k ? "on" : "",
      onClick: () => onMetric(k)
    }, label)))), overlays.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        margin: "0 0 6px",
        fontSize: "11px"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        color: "var(--plk-text-dim)"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: "14px",
        height: "0",
        borderTop: "2px solid var(--plk-accent)"
      }
    }), "Portfolio"), overlays.map(o => /*#__PURE__*/React.createElement("span", {
      key: `lg${o.key}`,
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        color: "var(--plk-text-dim)"
      },
      title: o.title || o.key
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: "14px",
        height: "0",
        borderTop: `2px dashed ${o.color}`
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        maxWidth: "160px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }, o.title || o.key)))), /*#__PURE__*/React.createElement("svg", {
      viewBox: `0 0 ${W} ${H}`,
      style: {
        width: "100%",
        display: "block"
      }
    }, gridV.map((v, i) => /*#__PURE__*/React.createElement("g", {
      key: i
    }, /*#__PURE__*/React.createElement("line", {
      x1: padL,
      x2: W - padR,
      y1: y(v),
      y2: y(v),
      stroke: "var(--plk-border)",
      strokeWidth: "1"
    }), /*#__PURE__*/React.createElement("text", {
      x: padL - 6,
      y: y(v) + 3,
      textAnchor: "end",
      fontSize: "9",
      fill: "var(--plk-text-dim)"
    }, isMoney ? usd(v) : num(v)))), overlays.map(o => {
      const opath = (o.series || []).map((p, i) => `${i ? "L" : "M"}${x(p.date).toFixed(1)},${y(Number(p[metric]) || 0).toFixed(1)}`).join(" ");
      return /*#__PURE__*/React.createElement("path", {
        key: `ov${o.key}`,
        d: opath,
        fill: "none",
        stroke: o.color,
        strokeWidth: "1.4",
        strokeDasharray: "4 3",
        opacity: "0.9"
      });
    }), /*#__PURE__*/React.createElement("path", {
      d: path,
      fill: "none",
      stroke: "var(--plk-accent)",
      strokeWidth: "1.8"
    }), partial ? /*#__PURE__*/React.createElement("path", {
      d: tailPath,
      fill: "none",
      stroke: "var(--plk-accent)",
      strokeWidth: "1.6",
      strokeDasharray: "4 3",
      opacity: "0.5"
    }) : null, series.map((p, i) => {
      const last = partial && i === series.length - 1;
      return /*#__PURE__*/React.createElement("circle", {
        key: i,
        cx: x(p.date),
        cy: y(Number(p[metric]) || 0),
        r: "2.4",
        fill: last ? "var(--plk-surface, #fff)" : "var(--plk-accent)",
        stroke: last ? "var(--plk-accent)" : "none",
        strokeWidth: "1.4",
        opacity: last ? 0.7 : 1
      }, /*#__PURE__*/React.createElement("title", null, `${shortDay(p.date)}${last ? " (today, so far)" : ""}: ${fmtV(Number(p[metric]) || 0)}`));
    }), events.map((e, i) => /*#__PURE__*/React.createElement("g", {
      key: `e${i}`
    }, /*#__PURE__*/React.createElement("line", {
      x1: x(e.date),
      x2: x(e.date),
      y1: H - padB,
      y2: H - padB - 8,
      stroke: channelColor(e.channel),
      strokeWidth: "2"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: x(e.date),
      cy: H - padB - 10,
      r: "3.4",
      fill: channelColor(e.channel),
      style: {
        cursor: "default"
      }
    }, /*#__PURE__*/React.createElement("title", null, `${shortDay(e.date)} · ${chLabel(e.channel)}: ${e.title || ""}`)))), /*#__PURE__*/React.createElement("text", {
      x: padL,
      y: H - 6,
      fontSize: "9",
      fill: "var(--plk-text-dim)"
    }, shortDay(dates[0])), /*#__PURE__*/React.createElement("text", {
      x: W - padR,
      y: H - 6,
      textAnchor: "end",
      fontSize: "9",
      fill: "var(--plk-text-dim)"
    }, partial ? "Today" : shortDay(dates[dates.length - 1])), /*#__PURE__*/React.createElement("rect", {
      x: padL,
      y: padT,
      width: W - padL - padR,
      height: H - padT - padB,
      fill: "transparent",
      onMouseLeave: () => this.setState({
        hover: null
      }),
      onMouseMove: e => {
        const svg = e.currentTarget.ownerSVGElement;
        const r = svg.getBoundingClientRect();
        const vbX = (e.clientX - r.left) / r.width * W;
        let best = 0,
          bd = Infinity;
        series.forEach((p, i) => {
          const d = Math.abs(x(p.date) - vbX);
          if (d < bd) {
            bd = d;
            best = i;
          }
        });
        if (best !== this.state.hover) this.setState({
          hover: best
        });
      }
    }), this.state.hover != null && series[this.state.hover] && (() => {
      const p = series[this.state.hover];
      const px = x(p.date),
        py = y(Number(p[metric]) || 0);
      const label = `${day(p.date)}  ·  ${fmtV(Number(p[metric]) || 0)}`;
      const tw = Math.max(74, label.length * 5.6);
      const tx = Math.min(Math.max(px - tw / 2, padL), W - padR - tw);
      const ty = Math.max(padT, py - 30);
      return /*#__PURE__*/React.createElement("g", {
        style: {
          pointerEvents: "none"
        }
      }, /*#__PURE__*/React.createElement("line", {
        x1: px,
        x2: px,
        y1: padT,
        y2: H - padB,
        stroke: "var(--plk-text-dim)",
        strokeWidth: "1",
        strokeDasharray: "3 3"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: px,
        cy: py,
        r: "4",
        fill: "var(--plk-accent)",
        stroke: "#fff",
        strokeWidth: "1.5"
      }), /*#__PURE__*/React.createElement("rect", {
        x: tx,
        y: ty,
        width: tw,
        height: "20",
        rx: "4",
        fill: "var(--plk-text)",
        opacity: "0.92"
      }), /*#__PURE__*/React.createElement("text", {
        x: tx + tw / 2,
        y: ty + 13,
        textAnchor: "middle",
        fontSize: "10",
        fill: "#fff"
      }, label));
    })()), events.length > 0 && /*#__PURE__*/React.createElement("p", {
      style: noteStyle
    }, "Pins are sends, posts and publishes: hover one for the piece behind it."));
  }
}

// ── §4.6 the portfolio swimlanes (the mix view) ──────────────────────────
export function Swimlanes({
  lanes,
  metric,
  windowStart,
  windowEnd,
  onOpen,
  onToggle,
  selected,
  colorFor
}) {
  // lanes = trend groupBy=campaign lanes (each w/ activeStart/End, series, events)
  // §4.6 interaction: single-click toggles the lane as a trend overlay (up to
  // 3, color-coded via colorFor); double-click opens the campaign detail.
  const shown = (lanes || []).slice(0, 8);
  if (!shown.length) return null;
  const isSel = k => selected && selected.has(k);
  const t0 = new Date(windowStart).getTime(),
    t1 = new Date(windowEnd).getTime();
  const span = Math.max(1, t1 - t0);
  const px = d => Math.max(0, Math.min(100, (new Date(day(d)).getTime() - t0) / span * 100));
  const totals = shown.map(l => (l.series || []).reduce((a, p) => a + (Number(p[metric]) || 0), 0));
  const tmax = Math.max(1, ...totals);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: "10px"
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: "11px",
      letterSpacing: ".05em",
      textTransform: "uppercase",
      color: "var(--plk-text-dim)",
      margin: "0 0 6px"
    }
  }, "Campaigns in this window", onToggle ? /*#__PURE__*/React.createElement("span", {
    style: {
      textTransform: "none",
      letterSpacing: 0,
      fontWeight: 400
    }
  }, ": click to overlay on the chart (up to 3), double-click to open") : null), shown.map((l, i) => {
    const left = px(l.activeStart || windowStart);
    const right = px(l.activeEnd || windowEnd);
    const sel = isSel(l.key);
    return /*#__PURE__*/React.createElement("div", {
      key: l.key,
      onClick: () => onToggle ? onToggle(l.key) : onOpen && onOpen(l.key),
      onDoubleClick: () => onOpen && onOpen(l.key),
      style: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "4px 6px",
        margin: "0 -6px",
        borderRadius: "4px",
        background: sel ? "var(--plk-surface-alt)" : "transparent",
        cursor: onToggle || onOpen ? "pointer" : "default"
      },
      title: onToggle ? `Click to overlay · double-click to open ${l.title || l.key}` : `Open ${l.title || l.key}`
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: "160px",
        fontSize: "12px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: "6px"
      }
    }, sel && colorFor && /*#__PURE__*/React.createElement("span", {
      style: {
        flex: "none",
        width: "8px",
        height: "8px",
        borderRadius: "2px",
        background: colorFor(l.key)
      }
    }), l.title || l.key), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        position: "relative",
        height: "18px",
        background: "var(--plk-surface-alt)",
        borderRadius: "4px"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        position: "absolute",
        left: `${left}%`,
        width: `${Math.max(1.5, right - left)}%`,
        top: "3px",
        bottom: "3px",
        background: "rgba(255,99,88,.18)",
        border: "1px solid rgba(255,99,88,.5)",
        borderRadius: "3px"
      }
    }), (l.events || []).map((e, j) => /*#__PURE__*/React.createElement("div", {
      key: j,
      title: `${day(e.date)} · ${chLabel(e.channel)}: ${e.title || ""}`,
      style: {
        position: "absolute",
        left: `${px(e.date)}%`,
        top: "4px",
        width: "5px",
        height: "10px",
        borderRadius: "2px",
        background: channelColor(e.channel),
        transform: "translateX(-50%)"
      }
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        width: "90px"
      },
      title: `${num(totals[i])} in this window`
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: "8px",
        borderRadius: "3px",
        background: "var(--plk-accent)",
        width: `${Math.max(3, totals[i] / tmax * 100)}%`
      }
    })));
  }), (lanes || []).length > 8 && /*#__PURE__*/React.createElement("p", {
    style: noteStyle
  }, "+ ", num(lanes.length - 8), " more"));
}

// ── §4.2 the channel legs table ──────────────────────────────────────────
export function ChannelLegs({
  groups,
  tier
}) {
  // one row per channel leg, its own mini-funnel — "converts on email,
  // dies on social" readable in one glance. Audience metrics ONLY.
  if (!groups || !groups.length) return /*#__PURE__*/React.createElement("p", {
    className: "territory-hint"
  }, "No channel activity in this range.");
  return /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Impr."), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Reach"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Clicks"), tier >= 1 && /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Sessions"), tier >= 1 && /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Conv."), tier >= 2 && /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Orders"), tier >= 2 && /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, groups.map(g => /*#__PURE__*/React.createElement("tr", {
    key: g.key
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
    style: {
      display: "inline-block",
      width: "9px",
      height: "9px",
      borderRadius: "50%",
      background: channelColor(g.key),
      marginRight: "6px"
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      textTransform: "capitalize"
    }
  }, chLabel(g.key))), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.impressions)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.reach)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.clicks)), tier >= 1 && /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.sessions)), tier >= 1 && /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.conversions)), tier >= 2 && /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, cell(g.orders)), tier >= 2 && /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, Number(g.salesValue) > 0 ? usd(g.salesValue) : "—")))));
}